import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useLogActivityAccess from '../hooks/useLogActivityAccess';
import './JogoPronuncia.css';

const RADIUS       = 52;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const TIME_NORMAL  = 10;
const TIME_GRACE   = 15;

const XP_BASE = { beginner: 10, intermediate: 20 };

const BADGES = {
  first_word:  { emoji: '🎯', label: 'Primeira Palavra' },
  streak3:     { emoji: '🔥', label: 'Em Chamas (3x)' },
  streak5:     { emoji: '⚡', label: 'Raio (5x)' },
  streak10:    { emoji: '🌟', label: 'Mestre (10x)' },
  speed:       { emoji: '💨', label: 'Velocista' },
  score50:     { emoji: '🥈', label: '50 XP' },
  score100:    { emoji: '🏆', label: '100 XP' },
};

function normalize(str) {
  return str.toLowerCase().replace(/[^a-z]/g, '').trim();
}

function levenshtein(a, b) {
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
  );
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = a[i-1] === b[j-1]
        ? dp[i-1][j-1]
        : 1 + Math.min(dp[i-1][j], dp[i][j-1], dp[i-1][j-1]);
  return dp[m][n];
}

function checkPronunciation(target, heard) {
  const t = normalize(target);
  const h = normalize(heard);
  if (!h) return false;
  if (h.includes(t) || t.includes(h)) return true;
  const tolerance = Math.ceil(t.length * 0.35);
  return levenshtein(t, h) <= tolerance;
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function JogoPronuncia() {
  useLogActivityAccess('pronuncia');
  const navigate = useNavigate();

  const [phase,       setPhase]       = useState('intro');     // intro | playing | gameover
  const [nivel,       setNivel]       = useState('beginner');
  const [loadingWords,setLoadingWords]= useState(false);

  // Display state (safe to read in renders)
  const [currentWord, setCurrentWord] = useState(null);
  const [countdown,   setCountdown]   = useState(TIME_NORMAL);
  const [lives,       setLives]       = useState(3);
  const [streak,      setStreak]      = useState(0);
  const [xp,          setXp]          = useState(0);
  const [wordsOk,     setWordsOk]     = useState(0);
  const [bestStreak,  setBestStreak]  = useState(0);
  const [earnedBadges,setEarnedBadges]= useState([]);
  const [cardState,   setCardState]   = useState('');     // '' | correct | wrong
  const [resultMsg,   setResultMsg]   = useState(null);  // { ok, heard, xpGained, speedBonus, streakMult }
  const [isListening, setIsListening] = useState(false);
  const [graceMode,   setGraceMode]   = useState(false);
  const [badgeToast,  setBadgeToast]  = useState(null);
  const [pageFlash,   setPageFlash]   = useState('');    // '' | jp-correct | jp-wrong

  // Mutable game state ref — safe inside async callbacks (avoids stale closures)
  const g = useRef({});

  const audioRef    = useRef(new Audio());
  const audioCache  = useRef(new Map());
  const timerRef    = useRef(null);
  const recogRef    = useRef(null);
  const wordQueueRef= useRef([]);
  const wordIdxRef  = useRef(0);
  const mountedRef  = useRef(true);

  useEffect(() => { return () => { mountedRef.current = false; }; }, []);

  // ── TTS ──────────────────────────────────────────────────────────────────────
  async function playTTS(word) {
    const key = word;
    try {
      let url;
      if (audioCache.current.has(key)) {
        url = audioCache.current.get(key);
      } else {
        const res = await fetch('/synt/text-to-speech', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            text: word,
            languageCode: 'en-US',
            voiceName: 'en-US-Wavenet-C',
            pitch: 0,
          }),
        });
        if (!res.ok) return;
        const blob = await res.blob();
        url = URL.createObjectURL(blob);
        audioCache.current.set(key, url);
      }
      const a = audioRef.current;
      a.pause();
      a.src = url;
      await a.play();
    } catch (_) { /* ignore */ }
  }

  // ── Badge logic ───────────────────────────────────────────────────────────────
  function checkBadges(newBadges, newStreak, newXp, newWordsOk) {
    const toAdd = [];
    if (newWordsOk === 1 && !newBadges.includes('first_word')) toAdd.push('first_word');
    if (newStreak >= 3  && !newBadges.includes('streak3'))     toAdd.push('streak3');
    if (newStreak >= 5  && !newBadges.includes('streak5'))     toAdd.push('streak5');
    if (newStreak >= 10 && !newBadges.includes('streak10'))    toAdd.push('streak10');
    if (newXp >= 50     && !newBadges.includes('score50'))     toAdd.push('score50');
    if (newXp >= 100    && !newBadges.includes('score100'))    toAdd.push('score100');
    return toAdd;
  }

  function showBadgeToast(badgeKey) {
    const b = BADGES[badgeKey];
    if (!b) return;
    setBadgeToast(`${b.emoji} Conquista: ${b.label}!`);
    setTimeout(() => { if (mountedRef.current) setBadgeToast(null); }, 2700);
  }

  // ── Stop recognition + timer ──────────────────────────────────────────────────
  function stopListening() {
    clearInterval(timerRef.current);
    try { recogRef.current?.stop(); } catch (_) {}
    recogRef.current = null;
    if (mountedRef.current) setIsListening(false);
  }

  // ── Handle result (correct or wrong) — reads only from g.current ─────────────
  const handleResult = useCallback((correct, heard, elapsed) => {
    if (!mountedRef.current) return;
    if (g.current.handledRef) return;
    g.current.handledRef = true;

    stopListening();

    const { nivel: lvl, lives: curLives, streak: curStreak, xp: curXp,
            wordsOk: curWords, bestStreak: curBest, earnedBadges: curBadges } = g.current;

    if (correct) {
      // XP calculation
      const base       = XP_BASE[lvl] || 10;
      const speedBonus = elapsed <= 3 ? 5 : 0;
      let   mult       = 1;
      const ns         = curStreak + 1;
      if (ns >= 5)      mult = 2;
      else if (ns >= 3) mult = 1.5;
      const gained   = Math.round(base * mult) + speedBonus;
      const newXp    = curXp + gained;
      const newWords = curWords + 1;
      const newBest  = Math.max(curBest, ns);

      // Badge check
      const newBadges = [...curBadges];
      const toAdd     = checkBadges(newBadges, ns, newXp, newWords);
      toAdd.forEach(b => newBadges.push(b));

      // If speed badge not yet earned
      if (speedBonus > 0 && !newBadges.includes('speed')) {
        newBadges.push('speed');
        toAdd.push('speed');
      }

      // Update g.current
      g.current.streak       = ns;
      g.current.xp           = newXp;
      g.current.wordsOk      = newWords;
      g.current.bestStreak   = newBest;
      g.current.earnedBadges = newBadges;
      g.current.consecutiveLosses = 0;
      g.current.graceMode    = false;

      // Update display state
      setStreak(ns);
      setXp(newXp);
      setWordsOk(newWords);
      setBestStreak(newBest);
      setEarnedBadges(newBadges);
      setCardState('correct');
      setPageFlash('jp-correct');
      setResultMsg({ ok: true, heard, xpGained: gained, speedBonus, streakMult: mult });

      if (toAdd.length > 0) {
        setTimeout(() => { if (mountedRef.current) showBadgeToast(toAdd[0]); }, 400);
      }

      // Advance after 1.8s
      setTimeout(() => {
        if (!mountedRef.current) return;
        setCardState('');
        setPageFlash('');
        setResultMsg(null);
        advanceWord();
      }, 1800);

    } else {
      // Wrong / timeout
      const newLives = curLives - 1;
      const newConsec = (g.current.consecutiveLosses || 0) + 1;

      g.current.lives             = newLives;
      g.current.streak            = 0;
      g.current.consecutiveLosses = newConsec;
      g.current.graceMode         = true;

      setLives(newLives);
      setStreak(0);
      setGraceMode(true);
      setCardState('wrong');
      setPageFlash('jp-wrong');
      setResultMsg({ ok: false, heard });

      // Play correct pronunciation
      playTTS(g.current.currentWord?.palavra || '');

      if (newLives <= 0) {
        setTimeout(() => {
          if (!mountedRef.current) return;
          setCardState('');
          setPageFlash('');
          setResultMsg(null);
          endGame();
        }, 3000);
      } else {
        setTimeout(() => {
          if (!mountedRef.current) return;
          setCardState('');
          setPageFlash('');
          setResultMsg(null);
          advanceWord();
        }, 3000);
      }
    }
  }, []); // eslint-disable-line

  // ── Advance to next word ──────────────────────────────────────────────────────
  const advanceWord = useCallback(() => {
    if (!mountedRef.current) return;
    const queue = wordQueueRef.current;
    const nextIdx = wordIdxRef.current + 1;

    if (nextIdx >= queue.length) {
      // Refill queue
      wordQueueRef.current = shuffle(queue);
      wordIdxRef.current   = 0;
    } else {
      wordIdxRef.current = nextIdx;
    }

    startWord(wordQueueRef.current[wordIdxRef.current]);
  }, []); // eslint-disable-line

  // ── Start a word round ────────────────────────────────────────────────────────
  const startWord = useCallback((word) => {
    if (!mountedRef.current) return;
    const isGrace = g.current.graceMode;
    const time    = isGrace ? TIME_GRACE : TIME_NORMAL;
    const startTs = Date.now();

    g.current.currentWord  = word;
    g.current.handledRef   = false;
    g.current.startTs      = startTs;

    setCurrentWord(word);
    setCountdown(time);
    setCardState('');
    setResultMsg(null);
    setIsListening(false);

    // Countdown timer
    clearInterval(timerRef.current);
    let remaining = time;
    timerRef.current = setInterval(() => {
      remaining -= 1;
      if (mountedRef.current) setCountdown(remaining);
      if (remaining <= 0) {
        clearInterval(timerRef.current);
        handleResult(false, '', time);
      }
    }, 1000);

    // Start speech recognition after 300ms (let UI settle)
    setTimeout(() => {
      if (!mountedRef.current) return;
      startRecognition(word.palavra, startTs);
    }, 300);
  }, [handleResult]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Speech Recognition ────────────────────────────────────────────────────────
  function startRecognition(targetWord, startTs) {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return; // no mic support

    try {
      const recog = new SpeechRecognition();
      recog.lang            = 'en-US';
      recog.interimResults  = false;
      recog.maxAlternatives = 5;
      recogRef.current      = recog;

      recog.onstart = () => { if (mountedRef.current) setIsListening(true); };

      recog.onresult = (e) => {
        if (g.current.handledRef) return;
        const elapsed = (Date.now() - startTs) / 1000;
        let found = false;
        let bestHeard = '';
        for (let i = 0; i < e.results[0].length; i++) {
          const alt = e.results[0][i].transcript;
          if (i === 0) bestHeard = alt;
          if (checkPronunciation(targetWord, alt)) {
            found = true;
            bestHeard = alt;
            break;
          }
        }
        handleResult(found, bestHeard, elapsed);
      };

      recog.onerror = (e) => {
        if (e.error === 'no-speech' || e.error === 'aborted') return;
        if (!g.current.handledRef && mountedRef.current) {
          handleResult(false, '', 0);
        }
      };

      recog.onend = () => {
        if (mountedRef.current) setIsListening(false);
        // Restart if not yet handled (keeps listening until timeout)
        if (!g.current.handledRef && mountedRef.current && recogRef.current === recog) {
          try { recog.start(); } catch (_) {}
        }
      };

      recog.start();
    } catch (_) { /* ignore */ }
  }

  // ── End game ──────────────────────────────────────────────────────────────────
  const endGame = useCallback(async () => {
    stopListening();
    setPhase('gameover');

    const { xp: finalXp, wordsOk: finalWords, bestStreak: finalBest, earnedBadges: finalBadges } = g.current;

    try {
      await fetch('/api/pronuncia/progresso', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          xp:             finalXp,
          palavrasCorretas: finalWords,
          melhorStreak:   finalBest,
          badges:         finalBadges,
        }),
      });
    } catch (_) { /* ignore */ }
  }, []);

  // ── Start game ────────────────────────────────────────────────────────────────
  async function startGame(lvl) {
    setLoadingWords(true);
    try {
      const res = await fetch(`/api/pronuncia/palavras?nivel=${lvl}`, { credentials: 'include' });
      const data = await res.json();
      if (!Array.isArray(data) || data.length === 0) {
        alert('Nenhuma palavra disponível para este nível.');
        setLoadingWords(false);
        return;
      }

      const queue = shuffle(data);
      wordQueueRef.current = queue;
      wordIdxRef.current   = 0;

      // Init g.current
      g.current = {
        nivel:             lvl,
        lives:             3,
        streak:            0,
        xp:                0,
        wordsOk:           0,
        bestStreak:        0,
        earnedBadges:      [],
        consecutiveLosses: 0,
        graceMode:         false,
        wordQueue:         queue,
        wordIdx:           0,
        currentWord:       queue[0],
        handledRef:        false,
        startTs:           0,
      };

      setNivel(lvl);
      setLives(3);
      setStreak(0);
      setXp(0);
      setWordsOk(0);
      setBestStreak(0);
      setEarnedBadges([]);
      setGraceMode(false);
      setPageFlash('');
      setPhase('playing');
      setLoadingWords(false);

      setTimeout(() => startWord(queue[0]), 200);

    } catch (_) {
      alert('Erro ao carregar palavras. Tente novamente.');
      setLoadingWords(false);
    }
  }

  function restartGame() {
    stopListening();
    clearInterval(timerRef.current);
    setPhase('intro');
    setCurrentWord(null);
    setCardState('');
    setResultMsg(null);
    setPageFlash('');
    setGraceMode(false);
  }

  // ── Countdown ring ────────────────────────────────────────────────────────────
  const maxTime   = graceMode ? TIME_GRACE : TIME_NORMAL;
  const fraction  = countdown / maxTime;
  const dashOffset= CIRCUMFERENCE * (1 - fraction);
  const ringColor = countdown <= 3 ? '#ef4444' : countdown <= 5 ? '#f59e0b' : '#1e90ff';

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className={`jp-page ${pageFlash}`}>
      {badgeToast && <div className="jp-badge-toast">{badgeToast}</div>}

      {/* ── INTRO ── */}
      {phase === 'intro' && (
        <div className="jp-intro">
          <div className="jp-intro-icon"><i className="fas fa-microphone" /></div>
          <h1>Jogo de Pronúncia</h1>
          <p>Pronuncie a palavra em inglês antes do tempo acabar</p>

          {!window.SpeechRecognition && !window.webkitSpeechRecognition && (
            <div className="jp-warning">
              <i className="fas fa-exclamation-triangle" />
              Use Chrome ou Edge para reconhecimento de voz.
            </div>
          )}

          <div className="jp-rules">
            <div className="jp-rule"><span>❤️</span> 3 vidas — perca todas para terminar o jogo</div>
            <div className="jp-rule"><span>⏱️</span> 10 segundos para pronunciar cada palavra</div>
            <div className="jp-rule"><span>🛡️</span> Modo Graça: +5s após um erro</div>
            <div className="jp-rule"><span>🔥</span> Sequência de acertos = multiplicador de XP</div>
            <div className="jp-rule"><span>💨</span> Pronunciar em &lt;3s = +5 XP bônus</div>
            <div className="jp-rule"><span>🏆</span> Conquistas por marcos de streak e XP</div>
          </div>

          <div className="jp-level-select">
            <h3>Escolha o Nível</h3>
            <div className="jp-level-btns">
              <button
                className="jp-level-btn beginner"
                onClick={() => startGame('beginner')}
                disabled={loadingWords}
              >
                <i className="fas fa-seedling" />
                <span>Iniciante</span>
                <small>Palavras do dia a dia<br/>apple, water, house…</small>
              </button>
              <button
                className="jp-level-btn intermediate"
                onClick={() => startGame('intermediate')}
                disabled={loadingWords}
              >
                <i className="fas fa-bolt" />
                <span>Intermediário</span>
                <small>Palavras complexas<br/>comfortable, environment…</small>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── PLAYING ── */}
      {phase === 'playing' && currentWord && (
        <div className="jp-game-wrap">

          {/* Header */}
          <div className="jp-header">
            <div className="jp-lives">
              {[0,1,2].map(i => (
                <span key={i} className={`jp-heart${i >= lives ? ' lost' : ''}`}>❤️</span>
              ))}
            </div>
            <div className="jp-streak">
              <i className="fas fa-fire" /> {streak}
            </div>
            <div className="jp-xp">
              <i className="fas fa-star" /> {xp} XP
            </div>
          </div>

          {/* Grace mode tag */}
          {graceMode && (
            <div className="jp-grace-tag">
              <i className="fas fa-shield-alt" /> Modo Graça — +5s
            </div>
          )}

          {/* Word card */}
          <div className={`jp-word-card ${cardState}`}>
            <div className="jp-categoria">{currentWord.categoria}</div>
            <div className="jp-palavra">{currentWord.palavra}</div>
            {currentWord.ipa && <div className="jp-ipa">{currentWord.ipa}</div>}
          </div>

          {/* Countdown + mic — only when no result shown */}
          {!resultMsg && (
            <div className="jp-mic-area">
              <svg className="jp-countdown-svg" viewBox="0 0 120 120">
                {/* Track */}
                <circle cx="60" cy="60" r={RADIUS} fill="none"
                  stroke="rgba(255,255,255,0.06)" strokeWidth="8" />
                {/* Progress */}
                <circle cx="60" cy="60" r={RADIUS} fill="none"
                  stroke={ringColor} strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={CIRCUMFERENCE}
                  strokeDashoffset={dashOffset}
                  transform="rotate(-90 60 60)"
                  style={{ transition: 'stroke-dashoffset 1s linear, stroke 0.3s' }}
                />
                {/* Mic icon in center */}
                <foreignObject x="30" y="30" width="60" height="60">
                  <div className={`jp-mic-icon${isListening ? ' listening' : ''}`}
                       xmlns="http://www.w3.org/1999/xhtml">
                    <i className="fas fa-microphone" />
                  </div>
                </foreignObject>
              </svg>

              <div className="jp-countdown-num" style={{ color: ringColor }}>
                {countdown}s
              </div>
              {isListening && <div className="jp-listening-text">Ouvindo...</div>}
            </div>
          )}

          {/* Result message */}
          {resultMsg && (
            <div className={`jp-result-msg ${resultMsg.ok ? 'correct' : 'wrong'}`}>
              <div className="jp-result-icon">{resultMsg.ok ? '✅' : '❌'}</div>
              {resultMsg.ok ? (
                <>
                  <div>Correto! +{resultMsg.xpGained} XP</div>
                  {resultMsg.speedBonus > 0 && <span className="jp-speed-tag">💨 Velocista +5</span>}
                  {resultMsg.streakMult > 1  && <span className="jp-streak-tag">🔥 x{resultMsg.streakMult} multiplicador</span>}
                </>
              ) : (
                <>
                  <div>Tente novamente!</div>
                  {resultMsg.heard
                    ? <div className="jp-result-sub">Ouvimos: "{resultMsg.heard}"</div>
                    : <div className="jp-result-sub">Tempo esgotado</div>
                  }
                  <div className="jp-result-sub">Ouça a pronúncia correta ↑</div>
                </>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="jp-footer">
            <span>{wordsOk} palavra{wordsOk !== 1 ? 's' : ''} corretas</span>
            <span>{nivel === 'beginner' ? 'Iniciante' : 'Intermediário'}</span>
          </div>
        </div>
      )}

      {/* ── GAME OVER ── */}
      {phase === 'gameover' && (
        <div className="jp-gameover">
          <div className="jp-go-icon">🎮</div>
          <h2>Fim de Jogo!</h2>

          <div className="jp-go-stats">
            <div className="jp-go-stat">
              <span>{xp}</span>
              <small>XP Ganhos</small>
            </div>
            <div className="jp-go-stat">
              <span>{wordsOk}</span>
              <small>Acertos</small>
            </div>
            <div className="jp-go-stat">
              <span>{bestStreak}</span>
              <small>Melhor Streak</small>
            </div>
          </div>

          {earnedBadges.length > 0 && (
            <div className="jp-go-badges">
              <h4>Conquistas desta sessão</h4>
              <div className="jp-badges-row">
                {earnedBadges.map(key => BADGES[key] && (
                  <div key={key} className="jp-badge-item">
                    {BADGES[key].emoji}
                    <small>{BADGES[key].label}</small>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="jp-go-actions">
            <button className="jp-btn jp-btn-play" onClick={restartGame}>
              <i className="fas fa-redo" /> Jogar Novamente
            </button>
            <button className="jp-btn jp-btn-secondary" onClick={() => navigate('/atividades')}>
              <i className="fas fa-home" /> Atividades
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
